import {useApolloClient} from '@apollo/client';
import {
  useInboxBacklogSummaryQuery,
  useTransactionsNeedingBasisQuery,
  useTransactionsQuery,
} from '@transactions/graphql';
import Money from 'common/Money';
import {ChevronLeft, Import, Plus} from 'lucide-react';
import * as React from 'react';
import {Link, Navigate, useLocation, useNavigate, useSearchParams} from 'react-router-dom';
import {AmountDisplay} from 'src/AmountDisplay';
import {useBreadcrumb} from 'src/Breadcrumbs';
import {Button} from 'src/Button';
import {HeaderActions} from 'src/components/HeaderActions';
import {InboxBacklogCard} from 'src/InboxBacklogCard';
import {InboxCard} from 'src/InboxCard';
import {InboxNeedsBasisCard} from 'src/InboxNeedsBasisCard';
import {InboxOutstandingCard} from 'src/InboxOutstandingCard';
import {formatListDate} from 'src/lib/displayDate';
import Loading from 'src/Loading';
import {useModals} from 'src/Modals';
import {PopoverMenu} from 'src/PopoverMenu';
import PopoverMenuItem from 'src/PopoverMenuItem';
import {HeaderSyncButton} from 'src/RawTransactionListView/HeaderSyncButton';
import {ReviewList} from 'src/ReviewPage';
import {
  UpcomingBenefitCredits,
  useBenefitCreditRows,
} from 'src/UpcomingBenefitCredits';
import type {UnifiedTransactionFragment} from '@transactions/graphql';

// Page size for the needs-basis to-do; "Show more" grows the limit, so no
// actionable row is ever stuck behind the cap.
const NEEDS_BASIS_PAGE = 50;

const PendingRow = ({
  transaction,
}: {
  transaction: UnifiedTransactionFragment;
}) => {
  const isPayment = transaction.netAmount !== 0;
  return (
    <Link
      to={`/transactions/${transaction._id}`}
      className="border-border hover:bg-muted-foreground/10 -mx-4 grid grid-cols-[1fr_auto] items-center gap-x-3 border-t px-4 py-2.5"
    >
      <span className="flex min-w-0 items-center gap-2">
        <span className="bg-warning/20 text-warning rounded px-1.5 py-0.5 text-[0.7rem] font-medium">
          Pending
        </span>
        <span className="truncate text-sm font-medium">
          {isPayment ? transaction.payee : transaction.description}
        </span>
      </span>
      <span className="text-right text-sm tabular-nums">
        {isPayment && (
          <AmountDisplay
            variant="plain"
            amount={Money.fromInt(transaction.netAmount, transaction.currency)}
          />
        )}
      </span>
      <span className="text-muted-foreground col-span-2 text-xs">
        {formatListDate(transaction.date)}
      </span>
    </Link>
  );
};

/**
 * The inbox is a dashboard of jobs, one card each: review new raw
 * transactions (links into the /review workflow), pending transactions,
 * benefit credits to record, tracked credits/loans, and the historical
 * backlog. Standing cards (outstanding, backlog) render nothing when empty.
 */
export const InboxPage = () => {
  const client = useApolloClient();
  const navigate = useNavigate();
  // /inbox/review is the review card taking over the Inbox. Both routes
  // render this same element, so moving between them (the button, the
  // breadcrumb, Back) keeps the page mounted and the change animates.
  const location = useLocation();
  const [searchParams] = useSearchParams();
  // Normalized: the router also matches a trailing slash.
  const expanded = location.pathname.replace(/\/+$/, '') === '/inbox/review';

  // The two states share one scroller. Expanding starts the review at its
  // top; folding back returns the dashboard to where it was, not wherever
  // the review had scrolled to.
  const scrollerRef = React.useRef<HTMLDivElement>(null);
  const dashboardScroll = React.useRef(0);
  React.useEffect(() => {
    const scroller = scrollerRef.current;
    if (scroller == null) return;
    if (expanded) {
      dashboardScroll.current = scroller.scrollTop;
      scroller.scrollTop = 0;
    } else {
      scroller.scrollTop = dashboardScroll.current;
    }
  }, [expanded]);
  useBreadcrumb(
    expanded
      ? {label: 'Review', fallback: [{label: 'Inbox', href: '/'}]}
      : {label: 'Inbox', reset: true},
  );

  const {
    data: summaryData,
    error: summaryError,
    refetch: refetchSummary,
  } = useInboxBacklogSummaryQuery({
    // Render the cached dashboard instantly on revisits; the refresh lands
    // in place. network-only made every navigation a full-page spinner.
    fetchPolicy: 'cache-and-network',
  });
  const summary = summaryData?.inboxBacklogSummary;
  const frontierDate = summary?.frontierDate ?? null;

  const {
    data: pendingData,
    error: pendingError,
    refetch: refetchPending,
  } = useTransactionsQuery({
    fetchPolicy: 'cache-and-network',
    variables: {pending: true, startDate: null, endDate: null, limit: 100},
  });
  const pendingTransactions = pendingData?.transactions?.items ?? [];

  // Queried HERE, not inside the card: the "All caught up!" verdict below has
  // to be computed from the same data as the cards it sits above.
  const [needsBasisLimit, setNeedsBasisLimit] = React.useState(NEEDS_BASIS_PAGE);
  const {
    data: needsBasisData,
    error: needsBasisError,
    refetch: refetchNeedsBasis,
  } = useTransactionsNeedingBasisQuery({
    fetchPolicy: 'cache-and-network',
    variables: {limit: needsBasisLimit},
  });
  const needsBasisRows = needsBasisData?.transactions.items ?? [];

  const {rows: creditRows, refetch: refetchCreditRows} = useBenefitCreditRows();
  const modals = useModals();

  // New rows land in the review card, so refresh the counts and the
  // embedded list after adding or importing.
  const refreshAfterAdd = () => {
    void refetchSummary();
    void client.refetchQueries({include: ['AllRawTransactions']});
  };

  // Quick action for manual (e.g. cash) transactions.
  const openAddRawTransactionModal = () => {
    modals.createRawTransactionModal.openModal({onCreate: refreshAfterAdd});
  };

  const openImportModal = () => {
    modals.importTransactionsModal.openModal({onImport: refreshAfterAdd});
  };

  // A dated /inbox/review link predates the card taking over the Inbox: it
  // was a Backlog year's review, which lives in the Ledger now.
  if (expanded && (searchParams.has('start') || searchParams.has('end'))) {
    const next = new URLSearchParams(searchParams);
    next.set('layer', 'activity');
    next.set('view', 'review');
    return <Navigate to={`/ledger?${next.toString()}`} replace />;
  }

  // The other cards' data only matters when they're showing: an expanded
  // review never waits on (or fails for) jobs folded out of sight.
  const othersReady = pendingData !== undefined && needsBasisData !== undefined;
  const othersFailed = pendingError != null || needsBasisError != null;
  if (summary === undefined || (!expanded && !othersReady)) {
    // A failed initial query would otherwise leave the spinner forever
    if (summaryError || (!expanded && othersFailed)) {
      return (
        <div className="flex h-full flex-col items-center justify-center gap-3">
          <span className="text-muted-foreground text-sm">
            Couldn&apos;t load the inbox.
          </span>
          <Button
            variant="outline"
            onClick={() => {
              void refetchSummary();
              void refetchPending();
              void refetchNeedsBasis();
            }}
          >
            Retry
          </Button>
        </div>
      );
    }
    return (
      <div className="flex h-full items-center justify-center">
        <Loading size="medium" />
      </div>
    );
  }

  const recentCount = summary.recentCount;
  const jobCount =
    (recentCount > 0 ? 1 : 0) +
    (pendingTransactions.length > 0 ? 1 : 0) +
    (needsBasisRows.length > 0 ? 1 : 0) +
    (creditRows.length > 0 ? 1 : 0);

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* The add / sync / import actions ride on the breadcrumb row (portaled
          into the shared top bar) instead of a second header row, so the inbox
          reclaims that vertical space — it matters most on mobile. */}
      {!expanded && (
      <HeaderActions>
          {/* On a phone, the tab bar's Add does this. */}
          <Button
            variant="ghost"
            size="icon"
            className="max-sm:hidden"
            onClick={openAddRawTransactionModal}
            title="Add raw transaction"
          >
            <Plus className="size-5" />
          </Button>
          <HeaderSyncButton />
          <PopoverMenu>
            <PopoverMenuItem onSelect={openImportModal} iconLeft={<Import />}>
              Import
            </PopoverMenuItem>
          </PopoverMenu>
      </HeaderActions>
      )}
      <div ref={scrollerRef} className="min-h-0 flex-1 overflow-y-auto">
        <div className="flex flex-col gap-4 p-4">
          {(recentCount > 0 || expanded) && (
            <InboxCard
              title="Review new transactions"
              meta={`${recentCount} waiting${
                frontierDate ? ` · oldest ${formatListDate(frontierDate)}` : ''
              }`}
            >
              <div className="border-border -mx-4 border-t">
                <ReviewList variant="preview" expanded={expanded} />
              </div>
              {/* The card's footer unfolds it, and once it has the page,
                  folds it back (as the breadcrumb's "Inbox" and Back do). */}
              <div className="border-border border-t py-2.5">
                {expanded ? (
                  <Button variant="outline" className="w-full gap-1.5" onClick={() => navigate('/')}>
                    <ChevronLeft className="size-4" />
                    Back to Inbox
                  </Button>
                ) : (
                  <Button
                    variant="primary"
                    className="w-full"
                    onClick={() => navigate('/inbox/review')}
                  >
                    Review ({recentCount})
                  </Button>
                )}
              </div>
            </InboxCard>
          )}

          {/* Everything else folds away while the review card has the page. */}
          <div
            className="grid transition-[grid-template-rows] duration-300 ease-in-out motion-reduce:transition-none"
            style={{gridTemplateRows: expanded ? '0fr' : '1fr'}}
          >
            <div
              // Folded, these cards are out of sight and out of reach: no
              // tabbing or screen-reader visits to controls behind the review.
              // (React 18 has no `inert` prop, hence the callback ref.)
              ref={(el) => {
                if (el) el.inert = expanded;
              }}
              className="flex min-h-0 flex-col gap-4 overflow-hidden"
            >
              {othersReady && (
              <>

              <InboxNeedsBasisCard
                rows={needsBasisRows}
                hasMore={needsBasisData?.transactions.hasMore ?? false}
                onShowMore={() =>
                  setNeedsBasisLimit((current) => current + NEEDS_BASIS_PAGE)
                }
                onChanged={() => void refetchNeedsBasis()}
              />
              {pendingTransactions.length > 0 && (
                <InboxCard
                  title="Pending transactions"
                  meta={`${pendingTransactions.length}`}
                >
                  {pendingTransactions.map((tx) => (
                    <PendingRow key={tx._id} transaction={tx} />
                  ))}
                </InboxCard>
              )}

              {creditRows.length > 0 && (
                <InboxCard
                  title="Benefit credits to record"
                  meta={`${creditRows.length}`}
                >
                  <div className="border-border -mx-4 border-t">
                    <UpcomingBenefitCredits
                      rows={creditRows}
                      onRecorded={() => void refetchCreditRows()}
                      hideHeading
                    />
                  </div>
                </InboxCard>
              )}

              {jobCount === 0 && (
                <div className="text-muted-foreground py-10 text-center text-sm">
                  All caught up!
                </div>
              )}

              <InboxOutstandingCard />
              {summary.backlogCount > 0 && (
                <InboxBacklogCard summary={summary} />
              )}
              </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
